import { useState, useCallback, useEffect, useRef } from 'react';
import { Check, FlaskConical, Droplets, Timer as TimerIcon, AlertCircle } from 'lucide-react';
import { useExperiment } from '@/context';
import { Burette, Timer } from '@/components/lab';
import ErlenmeyerFlask from '@/components/lab/ErlenmeyerFlask';
import MagneticStirrer from '@/components/lab/MagneticStirrer';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Transferring Sample to Flask',
    content: 'The weighed powder sample is quantitatively transferred to an Erlenmeyer flask. Quantitative transfer means ensuring all of the sample is moved without loss, which is critical for accurate results.',
    whatHappens: 'The powder is carefully transferred from the weighing vessel to the flask using water to rinse any adhered particles.',
    observe: [
      'No powder lost during transfer',
      'Rinse weighing vessel into flask',
      'Powder may not dissolve immediately',
      'Swirl gently to begin dissolution'
    ]
  },
  2: {
    title: 'Oxygen-Free Water',
    content: 'Dissolve the sample in a mixture of 100 mL of oxygen-free (boiled) water. Oxygen in water can oxidize Vitamin C, giving falsely low results. We use freshly boiled, cooled water to minimize dissolved O₂.',
    whatHappens: '100 mL of recently boiled (and cooled) water is added to dissolve the sample.',
    observe: [
      'Water should be recently boiled and cooled',
      'Use 100 mL per flask',
      'Sample dissolves to form solution',
      'Insoluble binders may remain'
    ]
  },
  3: {
    title: 'Acidification (2 N H₂SO₄)',
    content: 'Add 25 mL of 2 N sulphuric acid. The acidic environment is necessary for the proper redox reaction between ascorbic acid and iodine. (What is the molarity of the sulphuric acid?)',
    whatHappens: '25 mL of 2 N sulphuric acid is added. The solution becomes acidic.',
    observe: [
      'Handle 2 N H₂SO₄ with care',
      'Add 25 mL per flask',
      'pH becomes strongly acidic',
      'Acid stabilizes vitamin C'
    ]
  },
  4: {
    title: 'Filling Burette with Iodine',
    content: 'The iodine solution (I₂) is the titrant. It is standardized at 0.10 N concentration. The iodine will oxidize the ascorbic acid in a direct titration. The burette allows precise measurement of volume delivered.',
    whatHappens: 'The burette is filled to the 0 mL mark (or slightly above) with the brown iodine solution.',
    observe: [
      'Iodine solution is brown/amber',
      'Fill carefully, avoid bubbles',
      'Record initial burette reading',
      'Check stopcock is closed'
    ]
  },
  5: {
    title: 'Blank Titration',
    content: 'Perform one blank titration first. Then, starting with the rapid titration flask, titrate each solution at once with 0.10 N iodine solution. The main reaction is: C₆H₈O₆ + I₂ → C₆H₆O₆ + 2HI.',
    whatHappens: 'A blank titration is performed first, then iodine is added rapidly until a persistent light yellow color appears, indicating we are nearing the endpoint.',
    observe: [
      'Perform blank titration first',
      'Then titrate rapidly',
      'Iodine color disappears on mixing',
      'Stop when light yellow persists'
    ]
  },
  6: {
    title: 'Adding Starch Near Endpoint',
    content: 'Add 3 mL of starch test solution as the end point is approached. Adding starch early can cause iodine to bind irreversibly, making the endpoint hard to see. Adding starch near the endpoint gives a sharp colour change.',
    whatHappens: '3 mL of starch test solution is added. The solution may show a slight blue tint if any excess iodine is present.',
    observe: [
      'Add 3 mL starch test solution',
      'Solution may turn slightly blue',
      'Starch enhances endpoint detection',
      'Now ready for dropwise titration'
    ]
  },
  7: {
    title: 'Starch-Iodine Complex',
    content: 'Continue adding I₂ dropwise. The final endpoint is a "dirty purple" that persists for more than 90 seconds. This is the first sign that all vitamin C has reacted. I₂ + Starch → Blue-black complex.',
    whatHappens: 'Iodine is added drop by drop until a permanent dirty purple color appears that does not fade.',
    observe: [
      'Add iodine drop by drop',
      'Stir after each drop',
      'Color may fade initially',
      'Endpoint: dirty purple persisting >90 s'
    ]
  },
  8: {
    title: 'Why 90 Seconds?',
    content: 'The endpoint colour (dirty purple) must persist for more than 90 seconds to be considered valid. If the colour fades, more ascorbic acid is present and being oxidized. A persistent colour means all Vitamin C has reacted. Repeat with the other samples.',
    whatHappens: 'The timer is started to verify the dirty purple color persists without fading.',
    observe: [
      'Dirty purple color should not fade',
      'Keep solution still during timing',
      'If color fades, add more iodine',
      '90 seconds confirms valid endpoint'
    ]
  },
  9: {
    title: 'Recording Volume',
    content: 'The final burette reading minus the initial reading gives the volume of iodine solution used. This volume is directly proportional to the amount of vitamin C in the sample.',
    whatHappens: 'The burette reading is recorded. The volume used will be used to calculate the vitamin C content.',
    observe: [
      'Read bottom of meniscus',
      'Record to 0.01 mL',
      'Calculate volume used',
      'Typical range: 20-30 mL'
    ]
  },
  10: {
    title: 'Completing All Samples',
    content: 'All four samples must be titrated to obtain replicate data. The average of these results provides the reported vitamin C content, and the spread indicates the precision of the method.',
    whatHappens: 'The process is repeated for all remaining samples.',
    observe: [
      'Reset for each new sample',
      'Clean flask between titrations',
      'Results should be similar',
      'Identify any outliers'
    ]
  }
};

// Color constants
const PALE_YELLOW = '#FFFDE7';
const LIGHT_YELLOW = '#FFF59D';
const IODINE_BROWN = '#8B4513';
const STARCH_PURPLE = '#4A0080';

interface TrialResult {
  sample: number;
  volumeI2: number | null;
  mgVitC: number | null;
  perTablet: number | null;
  valid: boolean;
}

function interpolateColor(color1: string, color2: string, factor: number): string {
  const hex = (c: string) => parseInt(c, 16);
  const r1 = hex(color1.slice(1, 3));
  const g1 = hex(color1.slice(3, 5));
  const b1 = hex(color1.slice(5, 7));
  const r2 = hex(color2.slice(1, 3));
  const g2 = hex(color2.slice(3, 5));
  const b2 = hex(color2.slice(5, 7));

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export default function Part2Titration() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const partState = state?.experimentData?.titration || {
    currentSample: 1,
    buretteVolume: 50,
    flaskColor: PALE_YELLOW,
    hasSample: false,
    hasWater: false,
    hasAcid: false,
    hasStarch: false,
    nearEndpoint: false,
    endpointReached: false,
    timerRunning: false,
    timerSeconds: 90,
    endpointValid: false,
    stopcockOpen: false,
    results: [
      { sample: 1, volumeI2: null, mgVitC: null, perTablet: null, valid: false },
      { sample: 2, volumeI2: null, mgVitC: null, perTablet: null, valid: false },
      { sample: 3, volumeI2: null, mgVitC: null, perTablet: null, valid: false },
      { sample: 4, volumeI2: null, mgVitC: null, perTablet: null, valid: false }
    ] as TrialResult[]
  };

  const [stirrerOn, setStirrerOn] = useState(false);
  const [titrating, setTitrating] = useState(false);
  const [titrationProgress, setTitrationProgress] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [timerValue, setTimerValue] = useState(90);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const currentSample = partState.currentSample;

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('titration', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Timer effect
  useEffect(() => {
    if (timerActive && timerValue > 0) {
      timerRef.current = setTimeout(() => {
        setTimerValue(prev => prev - 1);
      }, 1000);
    } else if (timerActive && timerValue === 0) {
      // Endpoint is valid - color persisted for 90 seconds!
      setTimerActive(false);
      updateState({ endpointValid: true });
      completeStep('titration-8');
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timerActive, timerValue, updateState, completeStep]);

  // Handle titration progress
  useEffect(() => {
    if (titrating && stirrerOn) {
      const interval = setInterval(() => {
        setTitrationProgress(prev => {
          if (prev >= 100) {
            setTitrating(false);
            return 100;
          }
          return prev + 1;
        });
      }, 80);
      return () => clearInterval(interval);
    }
  }, [titrating, stirrerOn]);

  // Update flask color based on titration progress
  useEffect(() => {
    if (partState.hasSample) {
      let newColor = PALE_YELLOW;

      if (titrationProgress > 0 && titrationProgress < 70) {
        // Gradual yellowing
        newColor = interpolateColor(PALE_YELLOW, LIGHT_YELLOW, titrationProgress / 70);
      } else if (titrationProgress >= 70 && !partState.hasStarch) {
        // Light yellow before starch
        newColor = LIGHT_YELLOW;
        if (!partState.nearEndpoint) {
          updateState({ nearEndpoint: true });
        }
      } else if (partState.hasStarch && titrationProgress >= 95) {
        // Purple/blue-black with starch at endpoint
        newColor = STARCH_PURPLE;
        if (!partState.endpointReached) {
          updateState({ endpointReached: true });
        }
      } else if (partState.hasStarch) {
        // Light yellow with starch, not yet at endpoint
        newColor = LIGHT_YELLOW;
      }

      updateState({ flaskColor: newColor });
    }
  }, [titrationProgress, partState.hasSample, partState.hasStarch, partState.nearEndpoint, partState.endpointReached, updateState]);

  const handleTransferSample = useCallback(() => {
    updateState({ hasSample: true, flaskColor: PALE_YELLOW });
    completeStep('titration-1');
  }, [updateState, completeStep]);

  const handleAddWater = useCallback(() => {
    if (partState.hasSample) {
      updateState({ hasWater: true });
      completeStep('titration-2');
    }
  }, [partState.hasSample, updateState, completeStep]);

  const handleAddAcid = useCallback(() => {
    if (partState.hasWater) {
      updateState({ hasAcid: true });
      completeStep('titration-3');
    }
  }, [partState.hasWater, updateState, completeStep]);

  const handleFillBurette = useCallback(() => {
    if (partState.hasAcid) {
      updateState({ buretteVolume: 50 });
      completeStep('titration-4');
    }
  }, [partState.hasAcid, updateState, completeStep]);

  const handlePreTitrate = useCallback(() => {
    if (isStepCompleted('titration-4')) {
      setTitrating(true);
      setStirrerOn(true);
      updateState({ stopcockOpen: true });
    }
  }, [isStepCompleted, updateState]);

  const handleStopPreTitrate = useCallback(() => {
    setTitrating(false);
    updateState({ stopcockOpen: false });

    if (titrationProgress >= 70) {
      completeStep('titration-5');
    }
  }, [titrationProgress, updateState, completeStep]);

  const handleAddStarch = useCallback(() => {
    if (partState.nearEndpoint) {
      updateState({ hasStarch: true });
      completeStep('titration-6');
    }
  }, [partState.nearEndpoint, updateState, completeStep]);

  const handleFinalTitrate = useCallback(() => {
    if (partState.hasStarch && !partState.endpointReached) {
      setTitrating(true);
      updateState({ stopcockOpen: true });
    }
  }, [partState.hasStarch, partState.endpointReached, updateState]);

  const handleStopFinalTitrate = useCallback(() => {
    setTitrating(false);
    updateState({ stopcockOpen: false });

    if (partState.endpointReached) {
      // Calculate volume used
      const volumeUsed = (titrationProgress / 100) * 30 + 10; // 10-40 mL range
      updateState({
        buretteVolume: 50 - volumeUsed
      });
      completeStep('titration-7');
    }
  }, [titrationProgress, partState.endpointReached, updateState, completeStep]);

  const handleStartTimer = useCallback(() => {
    if (partState.endpointReached && !partState.endpointValid) {
      setTimerActive(true);
      setTimerValue(90);
    }
  }, [partState.endpointReached, partState.endpointValid]);

  const handleRecordVolume = useCallback(() => {
    if (partState.endpointValid) {
      const volumeUsed = 50 - partState.buretteVolume;
      const newResults = [...partState.results];
      newResults[currentSample - 1] = {
        ...newResults[currentSample - 1],
        volumeI2: volumeUsed,
        valid: true
      };
      updateState({ results: newResults });
      completeStep('titration-9');
    }
  }, [partState.endpointValid, partState.buretteVolume, partState.results, currentSample, updateState, completeStep]);

  const handleNextSample = useCallback(() => {
    if (currentSample < 4) {
      updateState({
        currentSample: currentSample + 1,
        buretteVolume: 50,
        flaskColor: PALE_YELLOW,
        hasSample: false,
        hasWater: false,
        hasAcid: false,
        hasStarch: false,
        nearEndpoint: false,
        endpointReached: false,
        endpointValid: false,
        stopcockOpen: false
      });
      setTitrationProgress(0);
      setTimerValue(90);
    } else {
      completeStep('titration-10');
    }
  }, [currentSample, updateState, completeStep]);

  const getFlaskFillLevel = () => {
    if (partState.hasAcid) return 50;
    if (partState.hasWater) return 40;
    if (partState.hasSample) return 10;
    return 0;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part II: Iodometric Titration (Sample {currentSample}/4)</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Transfer Sample to Flask', hint: 'Add weighed sample to flask' },
            { id: 2, title: 'Add O₂-Free Water', hint: 'Add 100 mL of oxygen-free (boiled) water' },
            { id: 3, title: 'Add Sulphuric Acid', hint: 'Add 25 mL of 2 N H₂SO₄' },
            { id: 4, title: 'Fill Burette with I2', hint: 'Fill with 0.10 N iodine solution' },
            { id: 5, title: 'Perform Blank Titration First', hint: 'Perform one blank titration before samples' },
            { id: 6, title: 'Add Starch Indicator', hint: 'Add 3 mL of starch test solution near endpoint' },
            { id: 7, title: 'Titrate Dropwise to Endpoint', hint: 'Continue until dirty purple color persists >90 s' },
            { id: 8, title: 'Start 90-Second Timer', hint: 'Color must persist for 90 seconds' },
            { id: 9, title: 'Record Volume', hint: 'Record burette reading if valid' },
            { id: 10, title: 'Repeat for Remaining Samples', hint: 'Complete all 4 samples' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`titration-${step.id}`) ? 'completed' : ''}`}
            >
              <div className={`step-number ${isStepCompleted(`titration-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`titration-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Results Table */}
        <div className="data-table">
          <h4>Titration Results</h4>
          <table>
            <thead>
              <tr>
                <th>Sample</th>
                <th>Vol I2 (mL)</th>
                <th>Valid</th>
              </tr>
            </thead>
            <tbody>
              {partState.results.map((result: TrialResult, i: number) => (
                <tr key={i} className={i + 1 === currentSample ? 'current' : ''}>
                  <td>{result.sample}</td>
                  <td>{result.volumeI2 ? result.volumeI2.toFixed(2) : '-'}</td>
                  <td>{result.valid ? <Check size={14} color="#00A86B" /> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LabBench
        experimentName="Vitamin C Titration"
        completedCount={state?.completedSteps.filter(s => s.startsWith('titration-')).length || 0}
        totalSteps={10}
        equipment={['Burette', 'Erlenmeyer flask', 'Magnetic stirrer', 'Timer']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="titration-workspace">
          <div className="titration-setup">
            {/* Burette */}
            <div className="burette-station">
              <Burette
                volume={partState.buretteVolume}
                maxVolume={50}
                liquidColor={IODINE_BROWN}
                stopcock={partState.stopcockOpen ? 'open' : 'closed'}
                onStopcock={() => {
                  if (titrating) {
                    if (partState.hasStarch) {
                      handleStopFinalTitrate();
                    } else {
                      handleStopPreTitrate();
                    }
                  }
                }}
              />
              <span className="equipment-label">Iodine Solution (0.10 N)</span>
            </div>

            {/* Flask on Stirrer */}
            <div className="flask-assembly">
              <ErlenmeyerFlask
                size={250}
                fillLevel={getFlaskFillLevel()}
                fillColor={partState.flaskColor}
                hasStirBar={true}
                stirring={stirrerOn}
                label={`Sample ${currentSample}`}
                active={
                  !isStepCompleted('titration-1') ||
                  (!isStepCompleted('titration-2') && partState.hasSample) ||
                  (!isStepCompleted('titration-3') && partState.hasWater)
                }
                onClick={() => {
                  if (!isStepCompleted('titration-1')) {
                    handleTransferSample();
                  } else if (!isStepCompleted('titration-2') && partState.hasSample) {
                    handleAddWater();
                  } else if (!isStepCompleted('titration-3') && partState.hasWater) {
                    handleAddAcid();
                  }
                }}
              />
              <MagneticStirrer
                speed={5}
                isOn={stirrerOn}
                hasFlask={true}
                onClick={() => setStirrerOn(!stirrerOn)}
              />
            </div>

            {/* 90-Second Timer */}
            <div className="timer-station">
              <div className={`large-timer ${timerActive ? 'active' : ''} ${partState.endpointValid ? 'valid' : ''}`}>
                <TimerIcon size={32} />
                <div className="timer-display">
                  {Math.floor(timerValue / 60)}:{(timerValue % 60).toString().padStart(2, '0')}
                </div>
                <span className="timer-label">Endpoint Timer</span>
              </div>
              {partState.endpointValid && (
                <div className="valid-badge">
                  <Check size={16} />
                  Valid Endpoint
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {!isStepCompleted('titration-4') && partState.hasAcid && (
              <button className="action-btn primary" onClick={handleFillBurette}>
                <Droplets size={18} />
                Fill Burette with I2
              </button>
            )}

            {isStepCompleted('titration-4') && !isStepCompleted('titration-5') && (
              <button
                className={`action-btn ${titrating ? 'danger' : 'success'}`}
                onClick={titrating ? handleStopPreTitrate : handlePreTitrate}
              >
                {titrating ? 'Stop (Light Yellow Reached)' : 'Begin Pre-Titration'}
              </button>
            )}

            {isStepCompleted('titration-5') && !isStepCompleted('titration-6') && (
              <button className="action-btn primary" onClick={handleAddStarch}>
                <FlaskConical size={18} />
                Add Starch Indicator
              </button>
            )}

            {isStepCompleted('titration-6') && !isStepCompleted('titration-7') && (
              <button
                className={`action-btn ${titrating ? 'danger' : 'success'}`}
                onClick={titrating ? handleStopFinalTitrate : handleFinalTitrate}
              >
                {titrating ? 'Stop at Dirty Purple Endpoint' : 'Continue Titration Dropwise'}
              </button>
            )}

            {isStepCompleted('titration-7') && !isStepCompleted('titration-8') && !timerActive && (
              <button className="action-btn warning" onClick={handleStartTimer}>
                <TimerIcon size={18} />
                Start 90-Second Timer
              </button>
            )}

            {timerActive && (
              <div className="timer-warning">
                <AlertCircle size={20} />
                Waiting for color to persist... {timerValue}s remaining
              </div>
            )}

            {isStepCompleted('titration-8') && !isStepCompleted('titration-9') && (
              <button className="action-btn success" onClick={handleRecordVolume}>
                <Check size={18} />
                Record Volume: {(50 - partState.buretteVolume).toFixed(2)} mL
              </button>
            )}

            {isStepCompleted('titration-9') && !isStepCompleted('titration-10') && (
              <button className="action-btn primary" onClick={handleNextSample}>
                {currentSample < 4 ? `Proceed to Sample ${currentSample + 1}` : 'Complete All Titrations'}
              </button>
            )}
          </div>

          {/* Color Legend */}
          <div className="color-legend">
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: PALE_YELLOW }} />
              <span>Initial (Pale Yellow)</span>
            </div>
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: LIGHT_YELLOW }} />
              <span>Near Endpoint</span>
            </div>
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: STARCH_PURPLE }} />
              <span>Endpoint (Dirty Purple)</span>
            </div>
          </div>
        </div>
      </LabBench>

      <style>{`
        .titration-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 20px;
          width: 100%;
        }

        .titration-setup {
          display: flex;
          gap: 50px;
          align-items: flex-end;
        }

        .burette-station, .flask-assembly, .timer-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .large-timer {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 20px;
          background: var(--bg-elevated);
          border: 2px solid var(--border);
          border-radius: 12px;
          transition: all 0.3s;
        }

        .large-timer.active {
          border-color: #FFA500;
          background: rgba(255, 165, 0, 0.1);
        }

        .large-timer.valid {
          border-color: var(--secondary);
          background: rgba(0, 168, 107, 0.1);
        }

        .timer-display {
          font-size: 32px;
          font-weight: 700;
          font-family: 'Courier New', monospace;
          color: var(--text-primary);
        }

        .timer-label {
          font-size: 11px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .valid-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: var(--secondary);
          color: white;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary { background: var(--primary); color: white; }
        .action-btn.success { background: var(--secondary); color: white; }
        .action-btn.danger { background: var(--danger); color: white; }
        .action-btn.warning { background: #FFA500; color: white; }

        .action-btn:hover {
          transform: translateY(-2px);
        }

        .timer-warning {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          background: rgba(255, 165, 0, 0.2);
          border-radius: 8px;
          color: #FFA500;
          font-weight: 500;
        }

        .color-legend {
          display: flex;
          gap: 24px;
          padding: 12px 20px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .color-swatch {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: 1px solid rgba(255,255,255,0.2);
        }

        .data-table {
          margin-top: 16px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .data-table h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .data-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .data-table th, .data-table td {
          padding: 6px 8px;
          text-align: center;
          border-bottom: 1px solid var(--border);
        }

        .data-table tr.current {
          background: rgba(255, 90, 54, 0.1);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
